// Named, reusable validation rules referenced by string from journey configs.
//
// A rule returns an error message string when the value is invalid, or "" when
// it passes. Rules never fire on a blank value — emptiness is the exclusive
// concern of `required` / `requiredWhen`, so an optional Emirates ID doesn't
// complain until the user actually types something.
//
// Register a project-specific rule with `registerRule()` rather than editing
// this file, so product teams can extend validation without touching the core.

import { isBlank } from "./conditions";

/** @typedef {(value: *, params: Object, values: Record<string, *>) => string} RuleFn */

const asString = (v) => (v === undefined || v === null ? "" : String(v));
const digits = (v) => asString(v).replace(/\D/g, "");

// Year out of either a bare year ("2019") or a full date. Vehicle fields mix
// the two — model year is a year, first registration is a date — so rules that
// compare them have to accept both. Returns null when neither parses.
const yearOf = (v) => {
  const s = asString(v).trim();
  if (!s) return null;
  if (/^\d{4}$/.test(s)) return Number(s);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.getFullYear();
};

/** @type {Record<string, RuleFn>} */
const RULES = {
  email: (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(asString(v).trim())
      ? ""
      : "Enter a valid email address",

  // Orient and the travel provider both reject anything that isn't a UAE
  // mobile in 971 5X XXXXXXX form (12 digits, no leading +/0).
  uaeMobile: (v) =>
    /^9715\d{8}$/.test(digits(v))
      ? ""
      : "Enter a valid UAE mobile: 971 5X XXXXXXX (12 digits)",

  // 784-YYYY-NNNNNNN-C, accepted with or without the dashes.
  emiratesId: (v) =>
    /^784\d{12}$/.test(digits(v))
      ? ""
      : "Enter a valid Emirates ID: 784-YYYY-XXXXXXX-X",

  passport: (v) =>
    /^[A-Za-z0-9]{6,12}$/.test(asString(v).trim())
      ? ""
      : "Enter a valid passport number",

  numeric: (v) => (/^\d+$/.test(asString(v).trim()) ? "" : "Enter numbers only"),

  /** @type {RuleFn} params: { pattern, message } */
  regex: (v, params) => {
    const ok = new RegExp(params.pattern).test(asString(v));
    return ok ? "" : params.message || "Enter a valid value";
  },

  /** @type {RuleFn} params: { min, max } — string length. */
  length: (v, params) => {
    const len = asString(v).trim().length;
    if (params.min != null && len < Number(params.min)) {
      return `Must be at least ${params.min} characters`;
    }
    if (params.max != null && len > Number(params.max)) {
      return `Must be at most ${params.max} characters`;
    }
    return "";
  },

  /** @type {RuleFn} params: { min, max } — numeric range. */
  range: (v, params) => {
    const n = Number(v);
    if (Number.isNaN(n)) return "Enter a valid number";
    if (params.min != null && n < Number(params.min)) {
      return `Must be ${params.min} or more`;
    }
    if (params.max != null && n > Number(params.max)) {
      return `Must be ${params.max} or less`;
    }
    return "";
  },

  /** @type {RuleFn} params: { min, max } — age in whole years derived from a DOB. */
  age: (v, params) => {
    const dob = new Date(v);
    if (Number.isNaN(dob.getTime())) return "Enter a valid date";
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
    if (params.min != null && age < Number(params.min)) {
      return `Must be at least ${params.min} years old`;
    }
    if (params.max != null && age > Number(params.max)) {
      return `Must be ${params.max} years old or younger`;
    }
    return "";
  },

  /** Date must be today or later — trip start dates, policy inception. */
  futureDate: (v) => {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "Enter a valid date";
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d >= today ? "" : "Date cannot be in the past";
  },

  /** Document expiry must be in the future. */
  notExpired: (v) => {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "Enter a valid date";
    return d > new Date() ? "" : "This document has expired";
  },

  /** @type {RuleFn} params: { field } — must be on/after another field's date. */
  afterField: (v, params, values) => {
    const other = values?.[params.field];
    if (isBlank(other)) return "";
    const a = new Date(v);
    const b = new Date(other);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return "";
    return a >= b ? "" : "Must be on or after the earlier date";
  },

  /** @type {RuleFn} params: { field } — must equal another field (confirm inputs). */
  matchesField: (v, params, values) =>
    asString(v) === asString(values?.[params.field]) ? "" : "Values do not match",

  /**
   * @type {RuleFn} params: { field, max, message }
   * This value's year must be within `max` years of another field's year, in
   * either direction. The other field may live on a different step — the form
   * carries the whole case in `values`, not just the current step's fields.
   *
   * Orient applies exactly this rule to year-of-manufacture vs first
   * registration (verified against their UAT: a gap of 2 quotes, 3 is refused
   * as `FnValidateRegYearModelYear`). Checking it here turns a dead-end error
   * on the plans screen into an inline message on the field that is wrong.
   */
  withinYearsOf: (v, params, values) => {
    const a = yearOf(v);
    const b = yearOf(values?.[params.field]);
    // Nothing to compare against yet: the other field is `required`'s problem.
    if (a === null || b === null) return "";
    const max = Number(params.max ?? 0);
    if (Number.isNaN(max)) return "";
    return Math.abs(a - b) <= max
      ? ""
      : `Must be within ${max} year${max === 1 ? "" : "s"} of the related date`;
  },
};

/**
 * Add or override a rule. Call once at module load from product code.
 * @param {string} name
 * @param {RuleFn} fn
 */
export const registerRule = (name, fn) => {
  RULES[name] = fn;
};

/**
 * Run one rule (string shorthand or object form) against a value.
 * @param {import("./types").ValidationRule} rule
 * @param {*} value
 * @param {Record<string, *>} values
 * @returns {string} error message, or "" when valid
 */
export const runRule = (rule, value, values) => {
  if (isBlank(value)) return ""; // emptiness is `required`'s job, not a rule's
  const name = typeof rule === "string" ? rule : rule.rule;
  const fn = RULES[name];
  if (!fn) {
    if (process.env.NODE_ENV !== "production") {
      // Loud in dev so a typo in a journey config surfaces immediately rather
      // than silently disabling validation in production.
      console.error(`[form] Unknown validation rule "${name}"`);
    }
    return "";
  }
  const params = typeof rule === "string" ? {} : rule;
  const message = fn(value, params, values || {});
  if (!message) return "";
  return (typeof rule === "object" && rule.message) || message;
};

export const hasRule = (name) => Object.prototype.hasOwnProperty.call(RULES, name);
